/**
 * Control Card Template Configuration
 * 
 * Centralized configuration for PDF layout, typography, colors, and spacing.
 * Following 8px grid system from design system.
 */

export const CONTROL_CARD_CONFIG = {
  /**
   * Page dimensions (A4 in points: 210mm x 297mm)
   */
  page: {
    width: 595.28,
    height: 841.89,
  },

  /**
   * Layout dimensions and positioning
   */
  layout: {
    tableStartX: 14,
    tableWidth: 180,
    margin: {
      top: 40,
      bottom: 8,
      left: 14,
      right: 14,
    },
  },

  /**
   * Typography scale
   * Following 8px grid system where possible
   */
  typography: {
    companyHeader: {
      size: 16,
      font: 'helvetica' as const,
      weight: 'bold' as const,
    },
    title: {
      size: 14,
      font: 'helvetica' as const,
      weight: 'bold' as const,
    },
    subtitle: {
      size: 10,
      font: 'helvetica' as const,
      weight: 'normal' as const,
    },
    equipmentHeader: {
      size: 9,
      font: 'helvetica' as const,
      weight: 'bold' as const,
    },
    label: {
      size: 8,
      font: 'helvetica' as const,
      weight: 'bold' as const,
    },
    body: {
      size: 8,
      font: 'helvetica' as const,
      weight: 'normal' as const,
    },
    tableHeader: {
      size: 6,
      font: 'helvetica' as const,
      weight: 'bold' as const,
    },
    small: {
      size: 5.5,
      font: 'helvetica' as const,
      weight: 'normal' as const,
    },
    footer: {
      size: 5,
      font: 'helvetica' as const,
      weight: 'normal' as const,
    },
    watermark: {
      size: 120,
      font: 'helvetica' as const,
      weight: 'bold' as const,
      sizeSecondary: 80,
    },
  },

  /**
   * Color palette (RGB tuples)
   */
  colors: {
    primary: [65, 65, 65] as [number, number, number],
    text: [65, 65, 65] as [number, number, number],
    darkGray: [50, 50, 50] as [number, number, number],
    border: [0, 0, 0] as [number, number, number],
    white: [255, 255, 255] as [number, number, number],
    watermark: [220, 220, 220] as [number, number, number], // Lighter for transparency effect
    black: [0, 0, 0] as [number, number, number],
    error: [255, 0, 0] as [number, number, number],
  },

  /**
   * Spacing scale (following 8px grid where possible)
   */
  spacing: {
    headerAfter: 6, // Reduced from 10, increased from 5 to fix squishing
    rowHeight: 9, // Reduced from 10
    cellPadding: 0.5, // Reduced from 1
    cellPaddingLarge: 1.5, // Reduced from 2
    tablePadding: 2,
    signatureMinHeight: 20,
    signatureFallbackHeight: 15,
    remarksMinHeight: 12,
  },

  /**
   * Border widths
   */
  borders: {
    thin: 0.03,
    standard: 0.05,
  },

  /**
   * Logo positioning and sizing
   */
  logos: {
    company: {
      x: 14,
      y: 15,
      width: 35,
      height: 10,
    },
    certification: {
      x: 169,
      y: 15,
      width: 25,
      height: 16,
    },
  },

  /**
   * Table column configuration
   */
  table: {
    specificationColumns: 3,
    columnRatios: {
      reportOwner: [0.5, 0.5], // Report No / Owner
      inspection: [32/180, 76/180, 36/180, 36/180], // Date / Type / Status / Next
      testControl: [0.8, 0.2], // Tested acc. to / Checklist
      signature: [0.5, 0.5], // Performed by / Responsible
    },
  },

  /**
   * Signature image scaling
   */
  signature: {
    scale: 0.75,
    padding: 2,
  },

  /**
   * Watermark positioning - single centered watermark for cleaner preview
   */
  watermark: {
    positions: [
      { x: 0.5, y: 0.5 }, // Center only
    ],
  },
} as const

/**
 * Text content for the professional control card template
 */
export const CONTROL_CARD_TEXT = {
  no: {
    title: 'ATTEST FOR SAKKYNDIG KONTROLL',
    subtitle: 'REPORT OF THOROUGH EXAMINATION',
    headers: {
      reportNumber: 'Rapportnr./Report No.',
      equipmentOwner: 'Utstyrseier/Equipment owner',
      remarks: 'Merknader/Remarks',
      testedAccordingTo: 'Testet/kontrollert iht. - tested/inspected acc. to',
      checklist: 'Sjekkliste/Check list',
      examDate: 'Kont.dato/Exam. Date',
      examType: 'Kontrolltype/Type of Examination',
      status: 'Status',
      nextExam: 'Neste kont./Next Exam.',
      performedBy: 'Utført av',
      responsible: 'Ordreansvarlig',
    },
    regulatory: 'Kontrollert iht. forskrift 1360 §8-7. Ved status NC skal arbeidsgivers plikter etter forskrift om utførelse av arbeid §12-5 andre ledd følges når videre bruk ikke anbefales. Iht. AML §5-5 fjerde ledd kreves bruksanvisning på norsk.',
    statusCodes: 'OK-Ingen kommentarer/No comments • C-Kommentar/Comments • MO-Merknad/Memorandum • RC-Pålegg/Recommendation • NC-Avvik (Ikke godkjent)/Non-conformity',
    checklistDefault: 'Offshore lastebærere',
    watermark: 'UTKAST',
  },
  footer: 'STM Inspection AS | Org.nr./Entity ID: 997553608 | Nygårdsviken 1, 5165 LAKSEVÅG | +47 55 94 30 90 | inspection@stmas.no | stmi.no',
} as const

/**
 * Job type translations
 */
export const JOB_TYPE_TRANSLATIONS: Record<string, string> = {
  'Periodisk kontroll': 'Periodisk kontroll / Periodic control',
  '48 måneder': '48 måneder kontroll / 48 month examination',
  'NDT': 'NDT kontroll / NDT examination',
} as const

/**
 * Status display mappings
 */
export const STATUS_MAPPINGS: Record<string, string> = {
  'OK': 'OK - Godkjent/Approved',
  'C': 'C - Comment',
  'MO': 'MO - Memorandum',
  'RC': 'RC - Recommendation',
  'NC': 'NC - Not approved',
} as const

/**
 * Type guard to check if config is valid
 */
export function isValidConfig(config: any): config is typeof CONTROL_CARD_CONFIG {
  return (
    config &&
    typeof config.page?.width === 'number' &&
    typeof config.page?.height === 'number' &&
    typeof config.layout?.tableStartX === 'number'
  )
}
